import { useId, type ComponentProps, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '../lib/cn'
import { BeninFlag } from './brand'

/**
 * Maps the Figma `<Input text>` / `<Input textarea>` / `<Input phone>` sets
 * (Default / Hover / Error / Disable). See design/COMPONENTS.md.
 *
 * The wrapper owns label/error wiring so every field is announced correctly by
 * a screen reader — spec §INTERFACES UTILISATEUR requires screen-reader support.
 */

// Figma `<Input text>`: a 44px slate-100 fill with an 8px radius, 12px inset,
// 14/400 text and a slate-400 placeholder. No border — every frame in the file,
// mobile and desktop, draws the field this way.
const fieldBase =
  'w-full rounded-md px-3 text-sm text-slate-900 ' +
  // Background *and* ring, so focus arrives rather than snapping on: a field
  // that lights up as you reach it is the app acknowledging where you are.
  'placeholder:text-slate-400 transition-[background-color,box-shadow,color] ' +
  'duration-[--duration-fast] ease-[--ease-out-soft] ' +
  'disabled:cursor-not-allowed disabled:text-slate-400'

const fieldTone = (invalid: boolean) =>
  invalid
    ? 'bg-rose-50 ring-1 ring-rose-300 focus-visible:outline-danger'
    : 'bg-slate-100 hover:bg-slate-200/70 focus-visible:bg-white focus-visible:ring-brand-200 focus-visible:ring-2 focus-visible:outline-brand-600'

/**
 * The red asterisk the frames put after a required label.
 *
 * `aria-hidden` because `aria-required` on the control already says it, and an
 * asterisk read aloud as "star" tells a screen-reader user nothing. Callers
 * therefore pass a plain label — putting the asterisk in the label string too
 * produced "Nom * *" on screen, which is how this was found.
 */
function RequiredMark({ when }: { when?: boolean | undefined }) {
  if (!when) return null
  return (
    <span className="text-danger" aria-hidden="true">
      {' '}
      *
    </span>
  )
}

type FieldShellProps = {
  label?: string | undefined
  hint?: ReactNode | undefined
  error?: string | undefined
  required?: boolean | undefined
  htmlFor: string
  describedBy?: string | undefined
  children: ReactNode
}

function FieldShell({
  label,
  hint,
  error,
  required,
  htmlFor,
  describedBy,
  children,
}: FieldShellProps) {
  return (
    <div className="flex w-full flex-col gap-2">
      {label ? (
        <label htmlFor={htmlFor} className="text-xs text-slate-600">
          {label}
          <RequiredMark when={required} />
        </label>
      ) : null}
      {children}
      {error ? (
        <p
          id={`${describedBy ?? htmlFor}-error`}
          role="alert"
          className="text-danger animate-slide-up text-xs"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={`${describedBy ?? htmlFor}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export type InputProps = Omit<ComponentProps<'input'>, 'size'> & {
  label?: string | undefined
  hint?: ReactNode | undefined
  error?: string | undefined
}

export function Input({ label, hint, error, id, className, required, ...props }: InputProps) {
  const auto = useId()
  const inputId = id ?? auto
  const invalid = Boolean(error)
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      <input
        id={inputId}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        aria-required={required || undefined}
        className={cn(fieldBase, fieldTone(invalid), 'h-11', className)}
        required={required}
        {...props}
      />
    </FieldShell>
  )
}

export type TextareaProps = ComponentProps<'textarea'> & {
  label?: string | undefined
  hint?: ReactNode | undefined
  error?: string | undefined
}

export function Textarea({ label, hint, error, id, className, required, ...props }: TextareaProps) {
  const auto = useId()
  const inputId = id ?? auto
  const invalid = Boolean(error)
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      <textarea
        id={inputId}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        className={cn(fieldBase, fieldTone(invalid), 'min-h-24 py-2', className)}
        required={required}
        {...props}
      />
    </FieldShell>
  )
}

/**
 * The Figma `<Input text>` with a trailing chevron — the "Motif" field on the
 * signalement form (frame 4962:7516) and the filters that use the same shape.
 *
 * A native `<select>` rather than a custom listbox. On the phones this app is
 * built for, the platform picker is a full-height wheel that is easier to hit
 * and already localised; a hand-rolled dropdown would be smaller, less
 * accessible, and worse with a screen reader for no gain.
 */
export type SelectProps = Omit<ComponentProps<'select'>, 'size'> & {
  label?: string | undefined
  hint?: ReactNode | undefined
  error?: string | undefined
  /** Shown as a disabled first option, the way the frame's grey prompt reads. */
  placeholder?: string | undefined
}

export function Select({
  label,
  hint,
  error,
  id,
  className,
  required,
  placeholder,
  children,
  ...props
}: SelectProps) {
  const auto = useId()
  const inputId = id ?? auto
  const invalid = Boolean(error)
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      <div className="relative">
        <select
          id={inputId}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          aria-required={required || undefined}
          className={cn(
            fieldBase,
            fieldTone(invalid),
            // pr-10 leaves room for the chevron; appearance-none removes the
            // platform arrow so there are not two of them.
            'h-11 appearance-none pr-10',
            // An unchosen select shows the placeholder option, which the frame
            // draws in the same grey as an empty field. slate-600, not the
            // design's slate-400 or slate-500: unlike a placeholder attribute
            // this is real rendered text, and both of those fall under 4.5:1 on
            // the slate-100 fill.
            props.value === '' || props.value === undefined ? 'text-slate-600' : '',
            className,
          )}
          required={required}
          {...props}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2 text-slate-700"
          aria-hidden="true"
        />
      </div>
    </FieldShell>
  )
}

/**
 * Benin phone entry, matching the Figma `<Input phone>` instance exactly:
 * a 44px slate-100 field with no border, 12px left inset, the flag at 32x24,
 * then `+229` and the number separated by 10px.
 *
 * The country code is fixed presentation, never part of the editable value, so
 * users cannot delete it and produce an unparseable number. Emits E.164
 * (+229XXXXXXXX) via onValueChange.
 */
export type PhoneInputProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  label?: string | undefined
  error?: string | undefined
  hint?: ReactNode | undefined
  value: string
  onValueChange: (e164: string) => void
}

const BJ_DIAL = '+229'

export function PhoneInput({
  label,
  error,
  hint,
  id,
  className,
  required,
  value,
  onValueChange,
  placeholder = '00 00 00 00 00',
  ...props
}: PhoneInputProps) {
  const auto = useId()
  const inputId = id ?? auto
  const invalid = Boolean(error)
  const national = value.startsWith(BJ_DIAL) ? value.slice(BJ_DIAL.length) : value

  return (
    <div className="flex w-full flex-col gap-2">
      {label ? (
        <label htmlFor={inputId} className="text-xs text-slate-600">
          {label}
          <RequiredMark when={required} />
        </label>
      ) : null}

      <div
        className={cn(
          'flex h-11 items-center gap-2.5 rounded-md pl-3 transition-colors',
          invalid ? 'bg-rose-50 ring-1 ring-rose-300' : 'bg-slate-100',
          'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2',
          invalid ? 'focus-within:outline-danger' : 'focus-within:outline-brand-600',
        )}
      >
        <BeninFlag />
        <span className="text-sm text-slate-900 select-none" aria-hidden="true">
          {BJ_DIAL}
        </span>
        <input
          id={inputId}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          aria-required={required || undefined}
          value={national}
          placeholder={placeholder}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
            onValueChange(digits ? `${BJ_DIAL}${digits}` : '')
          }}
          className={cn(
            'h-11 min-w-0 flex-1 rounded-r-md bg-transparent pr-3 text-sm text-slate-900',
            'placeholder:text-slate-400 focus:outline-none',
            className,
          )}
          required={required}
          {...props}
        />
      </div>

      {error ? (
        <p id={`${inputId}-error`} role="alert" className="animate-slide-up text-danger text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
